import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  ImageIcon,
  Link,
  Maximize,
  Minimize,
  Palette,
  CircleDashed,
} from 'lucide-react';

// Preset toggle button icons
const BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';
const PRESET_ICONS = [
  '/assets/Icon4.png',
  '/assets/Icon7.svg',
  '/assets/Icon3.png',
  '/assets/Avatars/AV29.png',
  '/assets/Avatars/AV45.png',
  '/assets/Avatars/AV20.png',
  '/assets/Avatars/AV5.png',
  '/assets/Avatars/AV38.png',
  '/assets/Avatars/AV60.png',
  '/assets/Avatars/AV87.png',
  '/assets/Avatars/AV90.png',
  '/assets/Avatars/AV95.png',
  '/assets/ChatbotToggleButtons/Adriana O\'Sullivan.webp',
  '/assets/ChatbotToggleButtons/Aliah Lane.webp',
  '/assets/ChatbotToggleButtons/Alisa Hester.webp',
  '/assets/ChatbotToggleButtons/Amanda Lowery.webp',
  '/assets/ChatbotToggleButtons/Ammar Foley.webp',
  '/assets/ChatbotToggleButtons/Ashton Blackwell.webp',
  '/assets/ChatbotToggleButtons/Ava Bentley.webp',
  '/assets/ChatbotToggleButtons/Bailey Richards.webp',
  '/assets/ChatbotToggleButtons/Benedict Doherty.webp',
  '/assets/ChatbotToggleButtons/Blake Riley.webp',
  '/assets/ChatbotToggleButtons/Elisa Nishikawa.webp',
  '/assets/ChatbotToggleButtons/Ethan Valdez.webp',
  '/assets/ChatbotToggleButtons/Jonathan Kelly.webp',
  '/assets/ChatbotToggleButtons/Kari Rasmussen.webp',
  '/assets/ChatbotToggleButtons/Marco Gross.webp',
  '/assets/ChatbotToggleButtons/Owen Harding.webp',
  '/assets/ChatbotToggleButtons/Kaden Scott.webp',
  '/assets/ChatbotToggleButtons/Zaid Schwartz.webp',
].map(url => url.startsWith('http') ? url : `${BASE_URL}${url}`);

function ToggleButton({
  isEditMode,
  toggleButtonSize,
  setToggleButtonSize,
  toggleButtonIcon,
  setToggleButtonIcon,
  customToggleButtonIcon,
  setCustomToggleButtonIcon,
  toggleButtonIconSize,
  setToggleButtonIconSize,
  toggleButtonBackgroundColor,
  setToggleButtonBackgroundColor,
  // Outline props
  toggleButtonOutlineEnabled,
  setToggleButtonOutlineEnabled,
  toggleButtonOutlineColor,
  setToggleButtonOutlineColor,
  toggleButtonIconOffsetX,
  toggleButtonIconOffsetY,
  updateConfig,
}) {
  const [selectedIcon, setSelectedIcon] = useState(toggleButtonIcon || '');
  const [customIconUrl, setCustomIconUrl] = useState(customToggleButtonIcon || '');
  const [buttonSize, setButtonSize] = useState(toggleButtonSize || 'medium');
  const [iconSize, setIconSize] = useState(toggleButtonIconSize || '50'); // Default to 50 if undefined
  const [backgroundColor, setBackgroundColor] = useState(toggleButtonBackgroundColor || '#1a1a1a');
  const [iconOffsetX, setIconOffsetX] = useState(0); // New: X Offset
  const [iconOffsetY, setIconOffsetY] = useState(0); // New: Y Offset
  const [outlineEnabled, setOutlineEnabled] = useState(toggleButtonOutlineEnabled || false);
  const [outlineColor, setOutlineColor] = useState(toggleButtonOutlineColor || '#ffffff');
  const [isIconGridExpanded, setIsIconGridExpanded] = useState(false); // For expand/collapse icon grid

  // Load saved settings on component mount
  // Load saved settings on component mount and sync with props
  useEffect(() => {
    // If we have props provided (e.g. from ChatbotWidget or Builder), they take precedence
    setButtonSize(toggleButtonSize || 'medium');
    setSelectedIcon(toggleButtonIcon ? (toggleButtonIcon.startsWith('/') ? `${BASE_URL}${toggleButtonIcon}` : toggleButtonIcon) : '');
    setCustomIconUrl(customToggleButtonIcon ? (customToggleButtonIcon.startsWith('/') ? `${BASE_URL}${customToggleButtonIcon}` : customToggleButtonIcon) : '');
    setIconSize(toggleButtonIconSize || '60');
    setBackgroundColor(toggleButtonBackgroundColor || '#1a1a1a');

    // Position & Outline - Ensure these update from props
    if (typeof toggleButtonIconOffsetX !== 'undefined') setIconOffsetX(toggleButtonIconOffsetX);
    if (typeof toggleButtonIconOffsetY !== 'undefined') setIconOffsetY(toggleButtonIconOffsetY);
    if (typeof toggleButtonOutlineEnabled !== 'undefined') setOutlineEnabled(toggleButtonOutlineEnabled);
    if (toggleButtonOutlineColor) setOutlineColor(toggleButtonOutlineColor);

    // Fallback to localStorage only if props are missing AND we're not in edit mode (rare case for legacy dev)
    if (!isEditMode && !toggleButtonIcon) {
      const savedConfig = JSON.parse(localStorage.getItem('chatbotConfig')) || {};
      if (savedConfig.toggleButtonSize) setButtonSize(savedConfig.toggleButtonSize);
      if (savedConfig.toggleButtonIcon) setSelectedIcon(savedConfig.toggleButtonIcon.startsWith('/') ? `${BASE_URL}${savedConfig.toggleButtonIcon}` : savedConfig.toggleButtonIcon);
      if (savedConfig.customToggleButtonIcon) setCustomIconUrl(savedConfig.customToggleButtonIcon.startsWith('/') ? `${BASE_URL}${savedConfig.customToggleButtonIcon}` : savedConfig.customToggleButtonIcon);
      if (savedConfig.toggleButtonIconSize) setIconSize(savedConfig.toggleButtonIconSize);
      if (savedConfig.toggleButtonBackgroundColor) setBackgroundColor(savedConfig.toggleButtonBackgroundColor);
      if (savedConfig.toggleButtonIconOffsetX) setIconOffsetX(savedConfig.toggleButtonIconOffsetX);
      if (savedConfig.toggleButtonIconOffsetY) setIconOffsetY(savedConfig.toggleButtonIconOffsetY);
      if (typeof savedConfig.toggleButtonOutlineEnabled !== 'undefined') setOutlineEnabled(savedConfig.toggleButtonOutlineEnabled);
      if (savedConfig.toggleButtonOutlineColor) setOutlineColor(savedConfig.toggleButtonOutlineColor);
    }
  }, [
    isEditMode,
    toggleButtonSize,
    toggleButtonIcon,
    customToggleButtonIcon,
    toggleButtonIconSize,
    toggleButtonBackgroundColor,
    toggleButtonIconOffsetX,
    toggleButtonIconOffsetY,
    toggleButtonOutlineEnabled,
    toggleButtonOutlineColor
  ]);

  const persistConfig = useCallback((updatedConfig) => {
    const savedConfig = JSON.parse(localStorage.getItem('chatbotConfig')) || {};
    localStorage.setItem('chatbotConfig', JSON.stringify({ ...savedConfig, ...updatedConfig }));
  }, []);

  const handleUpdate = (keyOrUpdates, value) => {
    let updatedConfig = {};

    if (typeof keyOrUpdates === 'object') {
      updatedConfig = keyOrUpdates;
    } else {
      updatedConfig = { [keyOrUpdates]: value };
    }

    // Update local state based on keys
    Object.entries(updatedConfig).forEach(([key, val]) => {
      switch (key) {
        case 'toggleButtonSize': setButtonSize(val); setToggleButtonSize(val); break;
        case 'toggleButtonIcon': setSelectedIcon(val); setToggleButtonIcon(val); break;
        case 'customToggleButtonIcon': setCustomIconUrl(val); setCustomToggleButtonIcon(val); break;
        case 'toggleButtonIconSize': setIconSize(val); setToggleButtonIconSize(val); break;
        case 'toggleButtonBackgroundColor': setBackgroundColor(val); setToggleButtonBackgroundColor(val); break;
        case 'toggleButtonIconOffsetX': setIconOffsetX(val); break;
        case 'toggleButtonIconOffsetY': setIconOffsetY(val); break;
        case 'toggleButtonOutlineEnabled': setOutlineEnabled(val); setToggleButtonOutlineEnabled(val); break;
        case 'toggleButtonOutlineColor': setOutlineColor(val); setToggleButtonOutlineColor(val); break;
        default: break;
      }
    });

    persistConfig(updatedConfig);
    updateConfig(updatedConfig);
  };



  // Logic to determine display values (Props vs Local)
  // In edit mode (Builder), we defer to the props which are the single source of truth from the parent state
  // This prevents race conditions where local state gets overwritten by useEffect syncing before parent state updates
  const effectiveIconOffsetX = isEditMode ? (toggleButtonIconOffsetX ?? 0) : iconOffsetX;
  const effectiveIconOffsetY = isEditMode ? (toggleButtonIconOffsetY ?? 0) : iconOffsetY;

  return (
    <div className="flex h-full w-full bg-slate-50/50 p-3 gap-3 overflow-hidden">
      <div className="w-full h-full flex flex-col bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 bg-white sticky top-0 z-10">
          <h2 className="text-lg font-bold tracking-tight text-slate-900">Toggle Button</h2>
          <p className="text-xs text-slate-500 font-medium mt-0.5">Customize the look of your chat widget's launcher.</p>
        </div>

        {/* Scrollable Settings */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="max-w-3xl mx-auto px-8 py-8 space-y-10">

            {/* 1. Icon Selection */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <ImageIcon size={18} className="text-slate-400" />
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-slate-900">Button Icon</label>
                  <p className="text-xs text-slate-500">Choose an icon or upload your own.</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Presets Grid */}
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {(isIconGridExpanded ? PRESET_ICONS : PRESET_ICONS.slice(0, 7)).map((url, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          handleUpdate({
                            toggleButtonIcon: url,
                            customToggleButtonIcon: ''
                          });
                        }}
                        className={`w-12 h-12 shrink-0 rounded-full overflow-hidden border-2 transition-all duration-200 hover:scale-105 bg-slate-900 ${selectedIcon === url && !customIconUrl
                          ? 'border-slate-900 ring-2 ring-slate-900/20'
                          : 'border-slate-200 hover:border-slate-300'
                          }`}
                      >
                        <img
                          src={url}
                          alt={`Icon ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                    {!isIconGridExpanded && PRESET_ICONS.length > 7 && (
                      <button
                        onClick={() => setIsIconGridExpanded(true)}
                        className="w-12 h-12 rounded-full border-2 border-dashed border-slate-300 hover:border-slate-400 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-all hover:scale-105"
                      >
                        <span className="text-xs font-bold">+{PRESET_ICONS.length - 7}</span>
                      </button>
                    )}
                  </div>
                  {isIconGridExpanded && (
                    <button
                      onClick={() => setIsIconGridExpanded(false)}
                      className="text-xs text-slate-400 hover:text-slate-600 font-medium"
                    >
                      Show less
                    </button>
                  )}
                </div>

                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-slate-100"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-2 text-[10px] font-bold tracking-wider text-slate-300 uppercase">Or</span>
                  </div>
                </div>

                {/* Custom URL Input */}
                <div className="flex gap-3">
                  {customIconUrl && (
                    <div className="w-10 h-10 rounded-lg border border-slate-200 bg-white flex items-center justify-center shrink-0 overflow-hidden">
                      <img src={customIconUrl} alt="Preview" className="w-6 h-6 object-contain" />
                    </div>
                  )}
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Link size={14} className="text-slate-400" />
                    </div>
                    <input
                      type="text"
                      value={customIconUrl}
                      onChange={(e) => handleUpdate({
                        customToggleButtonIcon: e.target.value,
                        toggleButtonIcon: ''
                      })}
                      placeholder="Or paste a custom icon URL (SVG/PNG)"
                      className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-0 transition-all placeholder:text-slate-400 text-slate-800"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Background Color */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <Palette size={18} className="text-slate-400" />
                </div>
                <div className="space-y-3 flex-1">
                  <label className="text-sm font-semibold text-slate-900">Background Color</label>
                  <div className="flex items-center gap-4 max-w-sm">
                    <div className="relative overflow-hidden w-11 h-11 rounded-full shadow-sm ring-1 ring-slate-200 shrink-0 group cursor-pointer">
                      <input
                        type="color"
                        value={backgroundColor}
                        onChange={(e) => handleUpdate('toggleButtonBackgroundColor', e.target.value)}
                        className="absolute inset-0 w-[150%] h-[150%] -top-[25%] -left-[25%] p-0 border-0 cursor-pointer"
                      />
                      <div className="absolute inset-0 ring-1 ring-black/5 rounded-full group-hover:bg-black/5 transition-colors pointer-events-none"></div>
                    </div>
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={backgroundColor}
                        onChange={(e) => handleUpdate('toggleButtonBackgroundColor', e.target.value)}
                        className="w-full pl-3 pr-3 py-2.5 text-sm font-mono bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-0 text-slate-700 uppercase"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Outline / Ring Section */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <CircleDashed size={18} className="text-slate-400" />
                </div>
                <div className="space-y-3 flex-1">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-slate-900">Ring / Outline</label>
                    <button
                      onClick={() => handleUpdate('toggleButtonOutlineEnabled', !outlineEnabled)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 ${outlineEnabled ? 'bg-slate-900' : 'bg-slate-200'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition duration-200 ease-in-out ${outlineEnabled ? 'translate-x-4.5' : 'translate-x-1'}`} style={{ transform: outlineEnabled ? 'translateX(18px)' : 'translateX(2px)' }} />
                    </button>
                  </div>

                  {outlineEnabled && (
                    <div className="flex items-center gap-4 max-w-sm ml-1">
                      <div className="relative overflow-hidden w-9 h-9 rounded-full shadow-sm ring-1 ring-slate-200 shrink-0 group cursor-pointer">
                        <input
                          type="color"
                          value={outlineColor}
                          onChange={(e) => handleUpdate('toggleButtonOutlineColor', e.target.value)}
                          className="absolute inset-0 w-[150%] h-[150%] -top-[25%] -left-[25%] p-0 border-0 cursor-pointer"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={outlineColor}
                          onChange={(e) => handleUpdate('toggleButtonOutlineColor', e.target.value)}
                          className="w-full pl-3 pr-3 py-2 text-sm font-mono bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-0 text-slate-700 uppercase"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <hr className="border-slate-100" />

            {/* 2. Styling Controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">

              {/* Button Size */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <Maximize size={18} className="text-slate-400" />
                  </div>
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-semibold text-slate-900">Button Size</label>
                      <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded uppercase">{buttonSize}</span>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      {['small', 'medium', 'large'].map((size) => (
                        <button
                          key={size}
                          onClick={() => handleUpdate('toggleButtonSize', size)}
                          className={`py-2 text-xs font-semibold rounded-lg border transition-all duration-200 capitalize ${buttonSize === size
                            ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                            }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Icon Size Slider */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <Minimize size={18} className="text-slate-400" />
                  </div>
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-semibold text-slate-900">Icon Adjustments</label>
                      <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{iconSize}%</span>
                    </div>

                    {/* Scale */}
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-[10px] text-slate-400 font-medium">
                        <span>Scale</span>
                      </div>
                      <input
                        type="range"
                        min="20"
                        max="200"
                        value={iconSize}
                        onChange={(e) => handleUpdate('toggleButtonIconSize', e.target.value)}
                        className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-slate-900"
                      />
                    </div>

                    {/* Position Pad */}
                    <div className="space-y-3 pt-2">
                      <div className="flex items-center justify-between text-sm">
                        <label className="font-semibold text-slate-900">Icon Postion</label>
                        <button
                          onClick={() => {
                            handleUpdate({
                              toggleButtonIconOffsetX: 0,
                              toggleButtonIconOffsetY: 0
                            });
                          }}
                          className="text-[10px] font-medium text-slate-500 hover:text-slate-900 bg-slate-100 px-2 py-1 rounded transition-colors"
                        >
                          Reset Center
                        </button>
                      </div>

                      <div className="flex gap-4">
                        {/* The Pad */}
                        <div
                          className="relative w-full h-32 bg-slate-50 rounded-xl border border-slate-200 cursor-crosshair overflow-hidden group select-none touch-none"
                          onMouseDown={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const handleMove = (clientX, clientY) => {
                              const x = clientX - rect.left;
                              const y = clientY - rect.top;

                              // Map 0 -> width to -50 -> 50
                              let offsetX = Math.round(((x / rect.width) * 100) - 50);
                              let offsetY = Math.round(((y / rect.height) * 100) - 50);

                              // Clamp values
                              offsetX = Math.max(-50, Math.min(50, offsetX));
                              offsetY = Math.max(-50, Math.min(50, offsetY));

                              handleUpdate({
                                toggleButtonIconOffsetX: offsetX,
                                toggleButtonIconOffsetY: offsetY
                              });
                            };

                            handleMove(e.clientX, e.clientY);

                            const onMouseMove = (moveEvent) => {
                              handleMove(moveEvent.clientX, moveEvent.clientY);
                            };

                            const onMouseUp = () => {
                              window.removeEventListener('mousemove', onMouseMove);
                              window.removeEventListener('mouseup', onMouseUp);
                            };

                            window.addEventListener('mousemove', onMouseMove);
                            window.addEventListener('mouseup', onMouseUp);
                          }}
                        >
                          {/* Grid Lines */}
                          <div className="absolute inset-0 pointer-events-none opacity-20"
                            style={{
                              backgroundImage: 'linear-gradient(to right, #94a3b8 1px, transparent 1px), linear-gradient(to bottom, #94a3b8 1px, transparent 1px)',
                              backgroundSize: '25% 25%',
                              backgroundPosition: 'center center'
                            }}
                          />

                          {/* Center Axies */}
                          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-300 transform -translate-x-1/2" />
                          <div className="absolute top-1/2 left-0 right-0 h-px bg-slate-300 transform -translate-y-1/2" />

                          {/* Draggable Handle */}
                          <div
                            className="absolute w-4 h-4 bg-slate-900 rounded-full shadow-lg border-2 border-white transform -translate-x-1/2 -translate-y-1/2 transition-transform duration-75 ease-out"
                            style={{
                              left: `${((parseInt(effectiveIconOffsetX || 0) + 50) / 100) * 100}%`,
                              top: `${((parseInt(effectiveIconOffsetY || 0) + 50) / 100) * 100}%`
                            }}
                          >
                            <div className="absolute inset-0 bg-slate-900 rounded-full animate-ping opacity-0 group-active:opacity-20" />
                          </div>
                        </div>
                      </div>

                      {/* Numeric Readout */}
                      <div className="flex gap-2">
                        <div className="flex-1 bg-slate-50 border border-slate-100 rounded px-2 py-1.5 flex justify-between items-center">
                          <span className="text-[10px] text-slate-400 font-bold uppercase">X</span>
                          <span className="text-xs font-mono text-slate-700">{effectiveIconOffsetX}px</span>
                        </div>
                        <div className="flex-1 bg-slate-50 border border-slate-100 rounded px-2 py-1.5 flex justify-between items-center">
                          <span className="text-[10px] text-slate-400 font-bold uppercase">Y</span>
                          <span className="text-xs font-mono text-slate-700">{effectiveIconOffsetY}px</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>



            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

ToggleButton.propTypes = {
  toggleButtonSize: PropTypes.string,
  setToggleButtonSize: PropTypes.func.isRequired,
  toggleButtonIcon: PropTypes.string,
  setToggleButtonIcon: PropTypes.func.isRequired,
  customToggleButtonIcon: PropTypes.string,
  setCustomToggleButtonIcon: PropTypes.func.isRequired,
  toggleButtonIconSize: PropTypes.string,
  setToggleButtonIconSize: PropTypes.func.isRequired,
  toggleButtonBackgroundColor: PropTypes.string,
  setToggleButtonBackgroundColor: PropTypes.func.isRequired,
  updateConfig: PropTypes.func.isRequired,
  toggleButtonOutlineEnabled: PropTypes.bool,
  setToggleButtonOutlineEnabled: PropTypes.func.isRequired,
  toggleButtonOutlineColor: PropTypes.string,
  setToggleButtonOutlineColor: PropTypes.func.isRequired,
  toggleButtonIconOffsetX: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  toggleButtonIconOffsetY: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isEditMode: PropTypes.bool,
};

export default ToggleButton;